/**
 * Analytics event tracker for `/*` surfaces.
 *
 * Backed by PostHog OSS (self-hosted, see terraform/posthog.tf). Events
 * also land as Sentry breadcrumbs so error reports keep the last few user
 * actions for context, and dev builds log to the console for verifying
 * instrumentation while building a feature.
 *
 * The canonical store is BigQuery (terraform/bigquery.tf) — PostHog is the
 * workspace UI on top. Every event is tagged with the five mandated
 * properties (organizationId, userId, tier, env, correlationId):
 *
 *   - userId         → posthog.identify(userId)            // distinct_id
 *   - organizationId → posthog.group("organization", id)   // $groups
 *   - tier / env / correlationId → posthog.register({ ... })   // sticky
 *
 * Call sites pass only event-specific properties; the five mandated
 * fields ride along automatically from the identity calls in
 * AuthContext.
 *
 * Use the typed wrappers (trackDiscover / trackDiagnose / trackDocument /
 * trackDesign / trackSettings / trackAuth / trackNavigation) rather than
 * raw trackEvent — they keep event categories consistent across surfaces.
 */

import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import { getEnv } from "@/config/env";

/** Broad grouping the event belongs to — drives breadcrumb category + event prefix. */
export type AnalyticsCategory =
  | "discover"
  | "diagnose"
  | "document"
  | "design"
  | "settings"
  // Activation funnel — the golden-loop milestones a PostHog funnel is built
  // from (signup → first value → invite → validated decision). See
  // ACTIVATION_EVENTS below for the canonical event names.
  | "activation"
  // Legacy categories kept so pre-IA-reframe call sites keep compiling.
  // New code should use diagnose / document / design instead.
  | "interview"
  | "prospect"
  | "navigation"
  | "auth";

/**
 * Canonical activation-funnel event names (P1 measurement). Emit via
 * `trackActivation(ACTIVATION_EVENTS.x, …)` so names never drift — the
 * PostHog activation funnel + time-to-value insights key on these exact
 * strings. Ordered by funnel stage.
 */
export const ACTIVATION_EVENTS = {
  /** Onboarding wizard finished (org set up). Prop: first_experience. */
  onboardingCompleted: "onboarding_completed",
  /** An Ignite analysis was launched — the first-value moment. Prop:
   *  source ('onboarding' | 'diagnose'), is_first_for_org. */
  analysisLaunched: "analysis_launched",
  /** A run finished and results are viewable. */
  resultsReady: "results_ready",
  /** A teammate was invited — the viral loop. */
  teamInviteSent: "team_invite_sent",
  /** An interview reached completion — first-party validation. */
  interviewCompleted: "interview_completed",
  /** An opportunity was accepted or rejected — a verified decision. Prop:
   *  decision ('accepted' | 'rejected'). */
  opportunityDecision: "opportunity_decision",
  /** A decision brief was generated for a run. Props: run_id, version. */
  briefGenerated: "brief_generated",
  /** An owner was assigned on a brief. Props: run_id, project_number. */
  briefOwnerAssigned: "brief_owner_assigned",
  /** A brief was shared out of the product. Props: run_id, channel. */
  briefShared: "brief_shared",
  /** The "invite a validator" CTA on a run overview was clicked — the
   *  upstream nudge toward `team_invite_sent`. Prop: run_id. */
  inviteValidatorCtaClicked: "invite_validator_cta_clicked",
} as const;

/**
 * The canonical activation event names, as a type.
 *
 * `trackActivation` accepts only these. It used to take a bare `string`, and
 * BriefTab duly emitted four raw literals that bypassed the constant entirely
 * (PLAT-492) — a typo there is invisible until someone notices a PostHog funnel
 * step reading zero, with no way to recover the lost events.
 */
export type ActivationEventName =
  (typeof ACTIVATION_EVENTS)[keyof typeof ACTIVATION_EVENTS];

/**
 * Primitive property types only — our sinks need to serialize these
 * to JSON, and we want to catch accidental Date / object leaks at the
 * type layer rather than at runtime.
 */
export type AnalyticsPropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined;

export type AnalyticsProperties = Record<string, AnalyticsPropertyValue>;

interface TrackOptions {
  category: AnalyticsCategory;
}

let initialized = false;

/**
 * Initialize the PostHog SDK. Safe to call once at app startup. No-ops if
 * the project key isn't configured (lets local dev run without analytics).
 *
 * env / correlationId are registered as sticky super-properties here so
 * every subsequent capture() carries them automatically.
 */
export function initAnalytics(): void {
  if (initialized) return;

  // Inline env reads — never capture at module load (see
  // feedback_module_level_env_capture: tests + runtime env changes
  // both break when env is frozen at import time).
  const apiKey = getEnv("VITE_POSTHOG_API_KEY") ?? "";
  const apiHost = getEnv("VITE_POSTHOG_HOST") ?? "";
  const env = getEnv("VITE_APP_ENV") ?? (import.meta.env?.DEV ? "dev" : "prod");

  if (!apiKey || !apiHost) {
    // Missing config is normal during local dev; analytics calls degrade
    // to breadcrumb-only. Don't throw — that'd break the app for
    // anyone running without the env vars.
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.debug(
        "[analytics] PostHog disabled — set VITE_POSTHOG_API_KEY + VITE_POSTHOG_HOST to enable",
      );
    }
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    // Mask user input by default; record only explicit captures.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    // Interview lock-in round 6 (F-23): our PostHog is the self-hosted OSS
    // build, which serves neither `/array/<key>/config.js` nor
    // `/array/<key>/config` — the SDK's flags/remote-config loader requested
    // both on every first document load and logged a 404 console-error pair
    // on every surface, rounds 3–6. We use no PostHog feature flags,
    // surveys, or site apps, so the fetch is disabled outright.
    advanced_disable_flags: true,
    persistence: "localStorage+cookie",
    loaded: (ph) => {
      ph.register({
        env,
        correlationId: generateCorrelationId(),
      });
    },
  });

  initialized = true;
}

/**
 * Identify the current user. Idempotent — call on session load and after
 * any user-change event. Don't call with an empty userId.
 */
export function identifyUser(
  userId: string,
  profile: { email?: string; tier?: string } = {},
): void {
  if (!initialized || !userId) return;
  const setProps: Record<string, AnalyticsPropertyValue> = {};
  if (profile.email) setProps.email = profile.email;
  if (profile.tier) setProps.tier = profile.tier;
  posthog.identify(userId, setProps);
  if (profile.tier) {
    // Register tier as a sticky super-property too — backend events
    // can't always group-identify, so having tier on every event
    // makes warehouse queries simpler.
    posthog.register({ tier: profile.tier });
  }
}

/**
 * Group-identify the user's current organization. PostHog group analytics
 * is the B2B funnel surface — orgs become first-class entities, not just
 * a property bag on users.
 */
export function groupOrganization(
  organizationId: string,
  attrs: { name?: string; tier?: string } = {},
): void {
  if (!initialized || !organizationId) return;
  const groupProps: Record<string, AnalyticsPropertyValue> = {};
  if (attrs.name) groupProps.name = attrs.name;
  if (attrs.tier) groupProps.tier = attrs.tier;
  posthog.group("organization", organizationId, groupProps);
}

/**
 * Reset the PostHog session — anonymises the distinct_id, clears
 * group identity, rotates the correlationId. Call on sign-out.
 */
export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
  // posthog.reset() clears registered super-properties as well; re-stamp
  // env + a fresh correlationId so the post-signout anonymous session
  // still tags events consistently.
  const env = getEnv("VITE_APP_ENV") ?? (import.meta.env?.DEV ? "dev" : "prod");
  posthog.register({
    env,
    correlationId: generateCorrelationId(),
  });
}

/**
 * Write an event to the analytics sink. Captures to PostHog AND a Sentry
 * breadcrumb (kept so error reports include the recent user actions).
 *
 * Event names should be `snake_case` + specific (e.g. `theme_clicked`,
 * `intent_dispatched`). Lower-cased and trimmed before send.
 */
export function trackEvent(
  name: string,
  properties: AnalyticsProperties | undefined,
  options: TrackOptions,
): void {
  const cleanName = name.trim().toLowerCase();
  const data = properties ? filterUndefined(properties) : undefined;

  // PostHog capture — primary sink. capture() is a no-op if init() never
  // ran, so missing config doesn't throw.
  if (initialized) {
    posthog.capture(cleanName, {
      category: options.category,
      ...(data ?? {}),
    });
  }

  // Sentry breadcrumb — keeps the last N user actions on error reports.
  Sentry.addBreadcrumb({
    category: `analytics.${options.category}`,
    message: cleanName,
    level: "info",
    data,
  });

  // Dev log — gated on Vite's DEV flag so production bundles stay quiet.
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.debug(
      `[analytics:${options.category}] ${cleanName}`,
      data ?? "",
    );
  }
}

function filterUndefined(
  properties: AnalyticsProperties,
): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [k, v] of Object.entries(properties)) {
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
}

function generateCorrelationId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (older browsers,
  // jsdom in some test configs). Not security-sensitive — this is just
  // a session correlation tag.
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ── Typed wrappers ──────────────────────────────────────────────────
// One thin helper per surface. The alternative — callers pass category
// each time — invites drift (`discover` vs `Discover` vs `new_discover`).

export function trackDiscover(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "discover" });
}

export function trackDiagnose(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "diagnose" });
}

export function trackDocument(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "document" });
}

export function trackDesign(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "design" });
}

export function trackSettings(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "settings" });
}

/**
 * Activation-funnel events (P1 measurement). Use the ACTIVATION_EVENTS
 * constants for the name so the PostHog funnel keys on stable strings.
 */
/**
 * Emit an activation-funnel event.
 *
 * The name is typed to `ActivationEventName` rather than `string` on purpose:
 * the PostHog funnel keys on these exact literals, so a raw string that drifts
 * by one character produces a step that silently reads zero. Passing an
 * unlisted name is now a compile error — add it to ACTIVATION_EVENTS first.
 */
export function trackActivation(
  name: ActivationEventName,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "activation" });
}

export function trackInterview(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "interview" });
}

export function trackProspect(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "prospect" });
}

export function trackNavigation(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "navigation" });
}

export function trackAuth(
  name: string,
  properties?: AnalyticsProperties,
): void {
  trackEvent(name, properties, { category: "auth" });
}
